import React, { useCallback, useEffect } from 'react';
import Grid from './Grid';
import Timer from './Timer';
import { GameMode, GameStatus } from '@/utils/gameState';
import { useTheme } from 'next-themes';
import { LetterEvaluation } from '@/utils/wordUtils';
import { useGame } from '@/context/GameContext';
import { useToast } from '@/hooks/use-toast';
import { FIRST_ROW_INDEX, LAST_ROW_INDEX } from '@/utils/gameState';
import { trackEvent } from '@/utils/analytics';

interface GameAreaProps {
  guesses: string[];
  evaluations: LetterEvaluation[][];
  currentGuess: string;
  revealed: boolean;
  gameMode: GameMode;
  timerActive: boolean;
  currentRowIndex: number;
  isDarkMode?: boolean;
}

const GameArea: React.FC<GameAreaProps> = ({
  guesses,
  evaluations,
  currentGuess,
  revealed,
  gameMode,
  currentRowIndex
}) => {
  const { resolvedTheme } = useTheme();
  const isDarkMode = resolvedTheme === 'dark';
  const { toast } = useToast();
  const { gameState, setGameState, revealAnimationComplete, setRevealAnimationComplete, setCurrentGuess, setTimerActive } = useGame();

  useEffect(() => {
    if (gameMode === GameMode.ENCORE && gameState.gameStatus === GameStatus.IN_PROGRESS) {
      const currentRowIndex = gameState.currentRowIndex;
      if (currentRowIndex > FIRST_ROW_INDEX && currentRowIndex < LAST_ROW_INDEX) {
        setTimerActive(true);
      } else {
        setTimerActive(false);
      }
    } else {
      setTimerActive(false);
    }
  }, [gameState.currentRowIndex, gameMode, setTimerActive, gameState.gameStatus]);

  // Triggers reveal animation upon new guess
  useEffect(() => {
    if (gameState.guesses.length > 0 && !revealAnimationComplete) {
      const lastGuessLength = gameState.guesses[gameState.guesses.length - 1].length;
      const timer = setTimeout(() => {
        setRevealAnimationComplete(true);
      }, lastGuessLength * 150 + 200);

      return () => clearTimeout(timer);
    }
  }, [gameState.guesses, revealAnimationComplete]);

  const handleTimerTimeout = useCallback(() => {
    if (gameMode === GameMode.ENCORE) {
      trackEvent('encore_timeout', {
        row_index: gameState.currentRowIndex + 1,
        game_mode: gameMode
      });

      const emptyGuess = '✕✕✕✕';
      const emptyEvaluation: LetterEvaluation[] = Array(4).fill('absent');
      
      const newGuesses = [...gameState.guesses, emptyGuess];
      const newEvaluations = [...gameState.evaluations, emptyEvaluation];

      setGameState(prev => {
        return {
          ...prev,
          guesses: newGuesses,
          evaluations: newEvaluations,
          currentRowIndex: prev.currentRowIndex + 1
        };
      });
      
      setCurrentGuess('');
      toast({
        title: "Time's up! You lost this try",
        variant: "destructive"
      });
    }
  }, [gameMode, gameState.guesses, gameState.evaluations, setCurrentGuess, setGameState, toast]);

  return (
    <div className="relative flex-grow flex py-2 items-center justify-center overflow-auto">
      <Grid 
        guesses={guesses} 
        evaluations={evaluations} 
        currentGuess={currentGuess}
        revealed={revealed}
        handleTimerTimeout={handleTimerTimeout}
        currentRowIndex={currentRowIndex}
      />
    </div>
  );
};

export default GameArea;
